import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Flex } from '@chakra-ui/react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import useIsLegalRegistrationNumberRequired from 'common/hooks/useIsLegalRegistrationNumberRequired';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import Heading from 'components/_core/Heading';
import Loader from 'components/Loader';
import { RichText } from 'features/Bento/atoms';
import { OBCard } from 'features/Bento/components';

import LockedIllustration from '../../../assets/locked-account.svg';
import Layout from '../../Layout';
import SoftBlockIllustration from './assets/traffic-light.svg';
import locales from './locales';
import { SiretWallIntroInfoDocument } from './siretWallIntroInfo.gql';

const HARD_BLOCK_LIMIT_MONTHS = 3;
const HARD_BLOCK_DELAYED_LIMIT_MONTHS = 4;

const HELP_BUTTON_LINK_URL =
  'https://www.shine.fr/blog/numero-siret-extrait-kbis-apres-envoi-dossier-creation-entreprise/';

const Intro = () => {
  const { companyProfileId } = useCompanyContext();
  const { data, loading } = useQuery(SiretWallIntroInfoDocument, {
    skip: !companyProfileId,
    variables: { companyProfileId: companyProfileId || '' },
  });
  const { canPostpone, postpone } = useIsLegalRegistrationNumberRequired(data);

  if (loading || !data) {
    return <Loader />;
  }

  const {
    viewer: {
      profile: { firstName },
    },
  } = data;

  const limitMonths =
    data.viewer.company.shineStart?.isRegistrationDelayed === true
      ? HARD_BLOCK_DELAYED_LIMIT_MONTHS
      : HARD_BLOCK_LIMIT_MONTHS;

  return (
    <Layout>
      <OBCard textAlign="center" width="100%">
        <OBCard.Illustration
          src={canPostpone ? SoftBlockIllustration : LockedIllustration}
        />

        <OBCard.Body>
          <Heading size="lg">
            {locales.formatString(locales.headline, {
              firstName: firstName || '',
            })}
          </Heading>

          <RichText>
            {`${
              canPostpone
                ? locales.formatString(locales.descriptionCanWait, {
                    months: limitMonths,
                  })
                : locales.descriptionCannotWait
            }`}
          </RichText>
          {canPostpone ? (
            <Callout marginTop="space-16">
              <Callout.Content>
                {locales.formatString(locales.warningBox, {
                  months: limitMonths,
                })}
              </Callout.Content>

              <Callout.Button
                as="a"
                href={HELP_BUTTON_LINK_URL}
                rel="noopener noreferrer"
                target="_blank"
              >
                {locales.helpButton}
              </Callout.Button>
            </Callout>
          ) : (
            <Button
              as="a"
              href={HELP_BUTTON_LINK_URL}
              marginTop="space-20"
              target="_blank"
              variant="inline-primary"
              width="100%"
            >
              {locales.helpButton}
            </Button>
          )}
        </OBCard.Body>
        <OBCard.Footer>
          <Flex align="center" direction="column">
            <Button as={Link} to="/siret-wall/form" width="100%">
              {locales.continue}
            </Button>
            {canPostpone ? (
              <Button
                marginTop="space-16"
                onClick={postpone}
                variant="secondary"
                width="100%"
              >
                {locales.remindLater}
              </Button>
            ) : null}
          </Flex>
        </OBCard.Footer>
      </OBCard>
    </Layout>
  );
};

export default Intro;
